/**
 * OrgChart Resource
 *
 * Thin 1:1 mapping to the org chart API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import type { HttpLayer } from '../http.js';
import type {
  Position,
  PositionTree,
  CreatePositionRequest,
  UpdatePositionRequest,
  MovePositionRequest,
  StaffPositionRequest,
} from '../types/index.js';

export class OrgChartResource {
  constructor(private http: HttpLayer) {}

  private prefix = '/v1/orgchart';

  async getOrgChart(): Promise<PositionTree> {
    return this.http.get(`${this.prefix}/tree`);
  }

  async listPositions(): Promise<Position[]> {
    return this.http.get(`${this.prefix}/positions`);
  }

  async getPosition(positionId: string): Promise<Position> {
    return this.http.get(`${this.prefix}/positions/${positionId}`);
  }

  /**
   * Create an OCCUPIED org-chart seat. A position is occupied by exactly one of:
   * a person (`user_id`), or an agent (`persona_id` AND `agent_process_id`).
   * There are no vacant seats and no half-staffed seats — omitting an occupant,
   * or naming a persona without its agent process, is a 422
   * (work_management ADR-032).
   *
   * The persona must already exist and name a provider, a model, a prompt and a
   * process (persona ADR-020). To create and staff in one call and have the
   * agent process resolved for you, use {@link OrgChartResource.staffPosition}.
   */
  async createPosition(params: CreatePositionRequest): Promise<Position> {
    return this.http.post(`${this.prefix}/positions`, params);
  }

  /**
   * Make a role executable: resolve an EXISTING persona, resolve an agent
   * process the same way `chat.create` does (explicit id → persona default →
   * seeded Simple), and write both occupants onto the position.
   *
   * This call does NOT invent a persona. A `persona_name` that does not already
   * exist is a 422 naming `persona.createPersonaWithConfig` — create the
   * persona first, with a complete config, then pass its `persona_id`
   * (work_management ADR-032, persona ADR-020).
   *
   * Pass `position_id` to occupy an existing row, or `title` + `role` +
   * `team_id` (and `parent_id` for non-CEO) to create and staff in one call.
   * Never returns a vacant or half-staffed position.
   */
  async staffPosition(params: StaffPositionRequest): Promise<Position> {
    return this.http.post(`${this.prefix}/positions/staff`, params);
  }

  async updatePosition(
    positionId: string,
    params: UpdatePositionRequest,
  ): Promise<Position> {
    return this.http.patch(
      `${this.prefix}/positions/${positionId}`,
      params,
    );
  }

  async movePosition(
    positionId: string,
    params: MovePositionRequest,
  ): Promise<Position> {
    return this.http.post(
      `${this.prefix}/positions/${positionId}/move`,
      params,
    );
  }

  async getChildren(positionId: string): Promise<Position[]> {
    return this.http.get(
      `${this.prefix}/positions/${positionId}/children`,
    );
  }

  async deletePosition(
    positionId: string,
  ): Promise<{ deleted: boolean }> {
    return this.http.delete(
      `${this.prefix}/positions/${positionId}`,
    );
  }
}
